import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useToast } from '@/hooks/use-toast';

interface SalesChannel {
  id: string;
  name: string;
  description: string;
  brand_id: string;
  created_at: string;
  updated_at: string;
}

interface GrowthPillar {
  id: string;
  name: string;
  description: string | null;
  sales_channel_id: string;
  created_at: string;
  updated_at: string;
}

interface GrowthMetric {
  id: string;
  name: string;
  category: string | null;
  growth_pillar_id: string;
}

interface GrowthStats {
  totalSalesChannels: number;
  totalGrowthPillars: number;
  northStarMetrics: number;
  supportiveMetrics: number;
}

interface GrowthOnePagerEditorContextType {
  isOpen: boolean;
  openEditor: () => void;
  closeEditor: () => void;
  // Persisted data
  salesChannels: SalesChannel[];
  growthPillars: GrowthPillar[];
  growthMetrics: GrowthMetric[];
  stats: GrowthStats;
  isLoading: boolean;
  refetch: () => void;
  getPillarsForChannel: (channelId: string) => GrowthPillar[];
  getMetricsForPillar: (pillarId: string) => GrowthMetric[];
}

const GrowthOnePagerEditorContext = createContext<GrowthOnePagerEditorContextType | undefined>(undefined);

export function GrowthOnePagerEditorProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [salesChannels, setSalesChannels] = useState<SalesChannel[]>([]);
  const [growthPillars, setGrowthPillars] = useState<GrowthPillar[]>([]);
  const [growthMetrics, setGrowthMetrics] = useState<GrowthMetric[]>([]);
  const [stats, setStats] = useState<GrowthStats>({
    totalSalesChannels: 0,
    totalGrowthPillars: 0,
    northStarMetrics: 0,
    supportiveMetrics: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  const { user } = useAuth();
  const { currentBrandId } = useBrandManager();
  const { toast } = useToast();

  const openEditor = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeEditor = useCallback(() => {
    setIsOpen(false);
  }, []);

  const fetchData = useCallback(async (brandId: string) => {
    if (!user || !brandId) {
      setSalesChannels([]);
      setGrowthPillars([]);
      setGrowthMetrics([]);
      setStats({
        totalSalesChannels: 0,
        totalGrowthPillars: 0,
        northStarMetrics: 0,
        supportiveMetrics: 0,
      });
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Fetch sales channels
      const { data: channelsData, error: channelsError } = await supabase
        .from('sales_channels')
        .select('id, name, description, brand_id, created_at, updated_at')
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });

      if (channelsError) throw channelsError;

      // Get sales channel IDs to filter pillars
      const channelIds = (channelsData || []).map(c => c.id);

      // Fetch growth pillars for these channels
      let pillarsData: GrowthPillar[] = [];
      if (channelIds.length > 0) {
        const { data: pillars, error: pillarsError } = await supabase
          .from('growth_pillars')
          .select('id, name, description, sales_channel_id, created_at, updated_at')
          .in('sales_channel_id', channelIds)
          .order('name', { ascending: true });

        if (pillarsError) throw pillarsError;
        pillarsData = (pillars || []) as GrowthPillar[];
      }

      // Fetch growth metrics for these pillars
      const pillarIds = pillarsData.map(p => p.id);
      let metricsData: GrowthMetric[] = [];
      if (pillarIds.length > 0) {
        const { data: metrics, error: metricsError } = await supabase
          .from('growth_metrics')
          .select('id, name, category, growth_pillar_id')
          .in('growth_pillar_id', pillarIds);

        if (metricsError) throw metricsError;
        metricsData = (metrics || []) as GrowthMetric[];
      }

      setSalesChannels((channelsData || []) as SalesChannel[]);
      setGrowthPillars(pillarsData);
      setGrowthMetrics(metricsData);

      // Calculate stats
      const northStarCount = metricsData.filter(m => m.category === 'North Star').length;
      const supportiveCount = metricsData.filter(m => m.category === 'Supportive Metric').length;

      setStats({
        totalSalesChannels: channelsData.length,
        totalGrowthPillars: pillarsData.length,
        northStarMetrics: northStarCount,
        supportiveMetrics: supportiveCount,
      });

      setHasFetched(true);
    } catch (error: any) {
      console.error('[GrowthOnePagerContext] Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch growth data",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [user, toast]);

  // Helper functions to filter data
  const getPillarsForChannel = useCallback((channelId: string) => {
    return growthPillars.filter(p => p.sales_channel_id === channelId);
  }, [growthPillars]);

  const getMetricsForPillar = useCallback((pillarId: string) => {
    return growthMetrics.filter(m => m.growth_pillar_id === pillarId);
  }, [growthMetrics]);

  // Track the last fetched brand to avoid re-fetching
  const [lastFetchedBrandId, setLastFetchedBrandId] = useState<string | null>(null);

  // Initial fetch when brand changes - only if we haven't fetched for this brand
  useEffect(() => {
    if (currentBrandId && user && currentBrandId !== lastFetchedBrandId) {
      setLastFetchedBrandId(currentBrandId);
      fetchData(currentBrandId);
    } else if (!currentBrandId) {
      setIsLoading(false);
    }
  }, [currentBrandId, user, lastFetchedBrandId, fetchData]);

  // Listen for brand changes
  useEffect(() => {
    const handleBrandChanged = () => {
      if (currentBrandId) {
        setLastFetchedBrandId(currentBrandId);
        fetchData(currentBrandId);
      }
    };

    window.addEventListener('brandChanged', handleBrandChanged);
    return () => {
      window.removeEventListener('brandChanged', handleBrandChanged);
    };
  }, [fetchData, currentBrandId]);

  const refetch = useCallback(() => {
    if (currentBrandId) {
      fetchData(currentBrandId);
    }
  }, [currentBrandId, fetchData]);

  return (
    <GrowthOnePagerEditorContext.Provider
      value={{
        isOpen,
        openEditor,
        closeEditor,
        salesChannels,
        growthPillars,
        growthMetrics,
        stats,
        isLoading,
        refetch,
        getPillarsForChannel,
        getMetricsForPillar,
      }}
    >
      {children}
    </GrowthOnePagerEditorContext.Provider>
  );
}

export function useGrowthOnePagerEditor() {
  const context = useContext(GrowthOnePagerEditorContext);
  if (!context) {
    throw new Error('useGrowthOnePagerEditor must be used within GrowthOnePagerEditorProvider');
  }
  return context;
}
